// AppRoutes.tsx or your main routes file
import React, { Suspense, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

// Redux hooks and actions
import { useAppDispatch } from '@store/hooks'
import {
  fetchAllTemplates,
  fetchAllTasks,
  fetchAllListings,
  fetchAllManagements,
} from '@store/actions'

// Local components
import ProtectedRoute from './protectedRoute'
import ScopeRoute from './ScopeRoute'
import { StaffScope } from '@constants/Settings/staffPermission.const'

// Main pages
import {
  Appointment,
  Dashboard,
  Message,
  Insurance,
  Notes,
  Files,
  Claims,
  Charting,
  Notifications,
  FormBuilder,
  NotFound,
} from '@pages/index'

// Dashboard pages
import {
  PerformanceBoard,
  MorningHuddle,
  ProviderPulse,
  CollectionsBoard,
  Scorecards,
  TrendAnalysis,
} from '@pages/Dashboard'
const TodayScheduleDetail = React.lazy(
  () => import('@pages/Dashboard/TodayScheduleDetail/TodayScheduleDetail.page'),
)
const TodayForecastDetail = React.lazy(
  () => import('@pages/Dashboard/TodayForecastDetail/TodayForecastDetail.page'),
)
const ARAgingDetail = React.lazy(
  () => import('@pages/Dashboard/ARAgingDetail/ARAgingDetail.page'),
)
const AROver90DayDetail = React.lazy(
  () => import('@pages/Dashboard/AROver90DayDetail/AROver90DayDetail.page'),
)
const ProductionDetail = React.lazy(
  () => import('@pages/Dashboard/ProductionDetail/ProductionDetail.page'),
)
const TreatmentAcceptanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/TreatmentAcceptanceDetail/TreatmentAcceptanceDetail.page'
    ),
)
const CollectionDetail = React.lazy(
  () => import('@pages/Dashboard/CollectionDetail/CollectionDetail.page'),
)
const CollectionPerformanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/CollectionPerformanceDetail/CollectionPerformanceDetail.page'
    ),
)
const InsurancePatientCollectionDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/InsurancePatientCollectionDetail/InsurancePatientCollectionDetail.page'
    ),
)
const DentistHygienistProductionDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/DentistHygienistProductionDetail/DentistHygienistProductionDetail.page'
    ),
)
const TreatmentValueDetail = React.lazy(
  () =>
    import('@pages/Dashboard/TreatmentValueDetail/TreatmentValueDetail.page'),
)
const FinanceClaimsDetail = React.lazy(
  () => import('@pages/Dashboard/FinanceClaimsDetail/FinanceClaimsDetail.page'),
)
const OutstandingBalanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/OutstandingBalanceDetail/OutstandingBalanceDetail.page'
    ),
)
const FinanceCollectionDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/FinanceCollectionDetail/FinanceCollectionDetail.page'
    ),
)
const CollectionRatioDetail = React.lazy(
  () =>
    import('@pages/Dashboard/CollectionRatioDetail/CollectionRatioDetail.page'),
)
const ProductionByDepartmentDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ProductionByDepartmentDetail/ProductionByDepartmentDetail.page'
    ),
)
const FinanceProductionByProcedureDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/FinanceProductionByProcedureDetail/FinanceProductionByProcedureDetail.page'
    ),
)
const FinanceProductionPerProviderDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/FinanceProductionPerProviderDetail/FinanceProductionPerProviderDetail.page'
    ),
)
const RevenueOverviewDetail = React.lazy(
  () =>
    import('@pages/Dashboard/RevenueOverviewDetail/RevenueOverviewDetail.page'),
)
const PracticeRevenueOverviewDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/PracticeRevenueOverviewDetail/PracticeRevenueOverviewDetail.page'
    ),
)
const RecareDetail = React.lazy(
  () => import('@pages/Dashboard/RecareDetail/RecareDetail.page'),
)
const TodaysProgressDetail = React.lazy(
  () =>
    import('@pages/Dashboard/TodaysProgressDetail/TodaysProgressDetail.page'),
)
const ChairTimeDetail = React.lazy(
  () => import('@pages/Dashboard/ChairTimeDetail/ChairTimeDetail.page'),
)
const CaseAcceptanceDetail = React.lazy(
  () =>
    import('@pages/Dashboard/CaseAcceptanceDetail/CaseAcceptanceDetail.page'),
)
const NoShowRateDetail = React.lazy(
  () => import('@pages/Dashboard/NoShowRateDetail/NoShowRateDetail.page'),
)
const SameDayCancellationDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/SameDayCancellationDetail/SameDayCancellationDetail.page'
    ),
)
const AppointmentOverrunDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/AppointmentOverrunDetail/AppointmentOverrunDetail.page'
    ),
)
const ProceduresDetail = React.lazy(
  () => import('@pages/Dashboard/ProceduresDetail/ProceduresDetail.page'),
)
const ProviderUtilizationDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ProviderUtilizationDetail/ProviderUtilizationDetail.page'
    ),
)
const IncompleteTreatmentPlanDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/IncompleteTreatmentPlanDetail/IncompleteTreatmentPlanDetail.page'
    ),
)
const OpenTreatmentPatientsDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/OpenTreatmentPatientsDetail/OpenTreatmentPatientsDetail.page'
    ),
)
const PatientFlowDetail = React.lazy(
  () => import('@pages/Dashboard/PatientFlowDetail/PatientFlowDetail.page'),
)
const RecallDetail = React.lazy(
  () => import('@pages/Dashboard/RecallDetail/RecallDetail.page'),
)
const PatientsDetail = React.lazy(
  () => import('@pages/Dashboard/PatientsDetail/PatientsDetail.page'),
)
const ProviderPerformanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ProviderPerformanceDetail/ProviderPerformanceDetail.page'
    ),
)
const YesterdaysPerformanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/YesterdaysPerformanceDetail/YesterdaysPerformanceDetail.page'
    ),
)
const HuddleYesterdayDetail = React.lazy(
  () =>
    import('@pages/Dashboard/HuddleYesterdayDetail/HuddleYesterdayDetail.page'),
)
const HuddleYesterdayScheduleDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/HuddleYesterdayDetail/HuddleYesterdayScheduleDetail.page'
    ),
)
const HuddleYesterdayPerformanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/HuddleYesterdayDetail/HuddleYesterdayPerformanceDetail.page'
    ),
)
const HuddleYesterdayProviderActualsDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/HuddleYesterdayDetail/HuddleYesterdayProviderActualsDetail.page'
    ),
)
const TomorrowForecastDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/TomorrowForecastDetail/TomorrowForecastDetail.page'
    ),
)
const TomorrowScheduleAtAGlanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/TomorrowScheduleAtAGlanceDetail/TomorrowScheduleAtAGlanceDetail.page'
    ),
)
const LeaderboardDetail = React.lazy(
  () => import('@pages/Dashboard/LeaderboardDetail/LeaderboardDetail.page'),
)
const SchedulingPerformanceDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/SchedulingPerformanceDetail/SchedulingPerformanceDetail.page'
    ),
)
const ScheduleOperationsDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ScheduleOperationsDetail/ScheduleOperationsDetail.page'
    ),
)
const InsuranceClaimsDetail = React.lazy(
  () =>
    import('@pages/Dashboard/InsuranceClaimsDetail/InsuranceClaimsDetail.page'),
)
const DiagnosisDetail = React.lazy(
  () => import('@pages/Dashboard/DiagnosisDetail/DiagnosisDetail.page'),
)
const NetHygieneProductionDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/NetHygieneProductionDetail/NetHygieneProductionDetail.page'
    ),
)
const ProductionPerHygieneVisitDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ProductionPerHygieneVisitDetail/ProductionPerHygieneVisitDetail.page'
    ),
)
const ExpensesBreakdownDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ExpensesBreakdownDetail/ExpensesBreakdownDetail.page'
    ),
)
const SeatUtilizationDetail = React.lazy(
  () =>
    import('@pages/Dashboard/SeatUtilizationDetail/SeatUtilizationDetail.page'),
)
const ClinicalEfficiencyDetail = React.lazy(
  () =>
    import(
      '@pages/Dashboard/ClinicalEfficiencyDetail/ClinicalEfficiencyDetail.page'
    ),
)
const PatientsAgeDetail = React.lazy(
  () => import('@pages/Dashboard/PatientsAgeDetail/PatientsAgeDetail.page'),
)
const PatientStatusDetail = React.lazy(
  () => import('@pages/Dashboard/PatientStatusDetail/PatientStatusDetail.page'),
)

// Claims pages
import { ClaimsInfo } from '@pages/Claims'

// Settings pages
import Settings from '@pages/Settings/Setting.page'
import Accounts from '@pages/Accounts/Accounts.page'
import SettingsRouteContent from '@pages/Settings/SettingsRouteContent.component'
import SettingsSubpageGate from '@pages/Settings/SettingsSubpageGate.component'
import { StripeOnboarding } from '@components/pages/Settings'
import {
  SETTINGS_ROUTE_ENTRIES,
  SCOPED_SETTINGS_ITEMS,
} from '@pages/Settings/settingsRoutes'

// Other pages
import Ui from '@pages/Ui/Ui.page'
import UiDesignSystem from '@pages/UiDesignSystem/UiDesignSystem.page'
import PatientManagement from '@pages/PatientManagement/PatientManagement.page'
import PatientProfile from '@pages/PatientProfile/PatientProfile.page'
import ProviderManagement from '@pages/Provider/ProviderManagement.page'
import MarketingManagement from '@pages/Marketing/MarketingManagement.page'
import PatientStatusTracker from '@pages/PatientStatusTracker/PatientStatusTracker.page'
import {
  MarketingOverview,
  MarketingCampaign,
  MarketingTemplate,
} from '@components/pages/Marketing'
import {
  Billing,
  BillingLedger,
  BillingInvoices,
  BillingPayments,
  BillingTransactions,
} from '@pages/Billing'
import ImageViewer from '@pages/ImageViewer/ImageViewer.page'
import ImageViewerList from '@pages/ImageViewer/ImageViewerList/ImageViewerList.page'
import ImageXrayViewer from '@pages/ImageViewer/ImageXrayViewer.page'
import Login from '@pages/Login/Login.page'
import LabPage from '@pages/Lab/Lab.page'
import KioskForm from '@pages/FormBuilder/KioskForm.page'
import PublicFormsViewPage from '@pages/FormBuilder/PublicFormsView.page'
import { NoteEditorOverlayHost } from '@components/common/NoteEditorOverlay'
import Reports from '@pages/Reports/Reports.page'
import ReportsHub from '@components/pages/Reports/ReportsHub.component'
import ReportsBundleSubmenuDetail from '@components/pages/Reports/ReportsBundleSubmenuDetail.component'
import ReportPreviewPage from '@pages/Reports/ReportPreview.page'
import CustomReportBuilderPage from '@pages/Reports/CustomReportBuilder.page'
import PresetsAutomationPage from '@pages/Reports/PresetsAutomation.page'
import ScheduledReportDetailPage from '@pages/Reports/ScheduledReportDetail.page'
import Membership from '@pages/Membership/Membership.page'

function AppRoutes(): React.ReactElement {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchAllTemplates())
    dispatch(fetchAllTasks())
    dispatch(fetchAllListings())
    dispatch(fetchAllManagements())
    return () => {}
  }, [dispatch])

  return (
    <>
      <Routes>
        <Route path="/" element={<ProtectedRoute />}>
          <Route
            path="/"
            element={
              <ScopeRoute
                require={StaffScope.DASHBOARD_READ}
                redirectTo="/appointment"
              >
                <Dashboard />
              </ScopeRoute>
            }
          >
            <Route index element={<PerformanceBoard />} />
            <Route
              path="dashboard/morning-huddle"
              element={<MorningHuddle />}
            />
            <Route
              path="dashboard/provider-pulse"
              element={<ProviderPulse />}
            />
            <Route
              path="dashboard/collections-board"
              element={<CollectionsBoard />}
            />
            <Route path="dashboard/scorecards" element={<Scorecards />} />
            <Route
              path="dashboard/trend-analysis"
              element={<TrendAnalysis />}
            />
            <Route
              path="dashboard/schedule"
              element={
                <Suspense fallback={null}>
                  <TodayScheduleDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/forecast"
              element={
                <Suspense fallback={null}>
                  <TodayForecastDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/ar-aging"
              element={
                <Suspense fallback={null}>
                  <ARAgingDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/ar-over-90-day"
              element={
                <Suspense fallback={null}>
                  <AROver90DayDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/production"
              element={
                <Suspense fallback={null}>
                  <ProductionDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/treatment-acceptance"
              element={
                <Suspense fallback={null}>
                  <TreatmentAcceptanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/treatment-value"
              element={
                <Suspense fallback={null}>
                  <TreatmentValueDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/collection"
              element={
                <Suspense fallback={null}>
                  <CollectionDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-collection"
              element={
                <Suspense fallback={null}>
                  <FinanceCollectionDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-collection-ratio"
              element={
                <Suspense fallback={null}>
                  <CollectionRatioDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-production-by-department"
              element={
                <Suspense fallback={null}>
                  <ProductionByDepartmentDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-production-by-procedure"
              element={
                <Suspense fallback={null}>
                  <FinanceProductionByProcedureDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-production-per-provider"
              element={
                <Suspense fallback={null}>
                  <FinanceProductionPerProviderDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/collection-performance"
              element={
                <Suspense fallback={null}>
                  <CollectionPerformanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/insurance-patient-collection"
              element={
                <Suspense fallback={null}>
                  <InsurancePatientCollectionDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/dentist-hygienist-production"
              element={
                <Suspense fallback={null}>
                  <DentistHygienistProductionDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/revenue-overview"
              element={
                <Suspense fallback={null}>
                  <RevenueOverviewDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/practice-revenue-overview"
              element={
                <Suspense fallback={null}>
                  <PracticeRevenueOverviewDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/recare"
              element={
                <Suspense fallback={null}>
                  <RecareDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/diagnosis"
              element={
                <Suspense fallback={null}>
                  <DiagnosisDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/todays-progress"
              element={
                <Suspense fallback={null}>
                  <TodaysProgressDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/chair-time"
              element={
                <Suspense fallback={null}>
                  <ChairTimeDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/case-acceptance"
              element={
                <Suspense fallback={null}>
                  <CaseAcceptanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/no-show-rate"
              element={
                <Suspense fallback={null}>
                  <NoShowRateDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/same-day-cancellation"
              element={
                <Suspense fallback={null}>
                  <SameDayCancellationDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/appointment-overrun"
              element={
                <Suspense fallback={null}>
                  <AppointmentOverrunDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/procedures"
              element={
                <Suspense fallback={null}>
                  <ProceduresDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/provider-utilization"
              element={
                <Suspense fallback={null}>
                  <ProviderUtilizationDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/incomplete-treatment-plan"
              element={
                <Suspense fallback={null}>
                  <IncompleteTreatmentPlanDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/open-treatment-patients"
              element={
                <Suspense fallback={null}>
                  <OpenTreatmentPatientsDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/patient-flow"
              element={
                <Suspense fallback={null}>
                  <PatientFlowDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/recall"
              element={
                <Suspense fallback={null}>
                  <RecallDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/patients"
              element={
                <Suspense fallback={null}>
                  <PatientsDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/provider-performance"
              element={
                <Suspense fallback={null}>
                  <ProviderPerformanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/yesterdays-performance"
              element={
                <Suspense fallback={null}>
                  <YesterdaysPerformanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/huddle-yesterday-actuals"
              element={
                <Suspense fallback={null}>
                  <HuddleYesterdayDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/huddle-yesterday-schedule"
              element={
                <Suspense fallback={null}>
                  <HuddleYesterdayScheduleDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/huddle-yesterday-performance"
              element={
                <Suspense fallback={null}>
                  <HuddleYesterdayPerformanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/huddle-yesterday-provider-actuals"
              element={
                <Suspense fallback={null}>
                  <HuddleYesterdayProviderActualsDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/huddle-tomorrow-forecast"
              element={
                <Suspense fallback={null}>
                  <TomorrowForecastDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/huddle-tomorrow-schedule"
              element={
                <Suspense fallback={null}>
                  <TomorrowScheduleAtAGlanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/leaderboard"
              element={
                <Suspense fallback={null}>
                  <LeaderboardDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/scheduling-performance"
              element={
                <Suspense fallback={null}>
                  <SchedulingPerformanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/schedule-operations"
              element={
                <Suspense fallback={null}>
                  <ScheduleOperationsDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/insurance-claims"
              element={
                <Suspense fallback={null}>
                  <InsuranceClaimsDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-claims"
              element={
                <Suspense fallback={null}>
                  <FinanceClaimsDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/finance-outstanding-balance"
              element={
                <Suspense fallback={null}>
                  <OutstandingBalanceDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/net-hygiene-production"
              element={
                <Suspense fallback={null}>
                  <NetHygieneProductionDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/production-per-hygiene-visit"
              element={
                <Suspense fallback={null}>
                  <ProductionPerHygieneVisitDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/expenses-breakdown"
              element={
                <Suspense fallback={null}>
                  <ExpensesBreakdownDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/seat-utilization"
              element={
                <Suspense fallback={null}>
                  <SeatUtilizationDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/clinical-efficiency"
              element={
                <Suspense fallback={null}>
                  <ClinicalEfficiencyDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/patients-age"
              element={
                <Suspense fallback={null}>
                  <PatientsAgeDetail />
                </Suspense>
              }
            />
            <Route
              path="dashboard/patient-status"
              element={
                <Suspense fallback={null}>
                  <PatientStatusDetail />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="/patient-status-tracker"
            element={<PatientStatusTracker />}
          />
          <Route
            path="/patient-profile/:patientId"
            element={
              <ScopeRoute require={StaffScope.PATIENTS_READ}>
                <PatientProfile />
              </ScopeRoute>
            }
          />
          <Route
            path="/appointment"
            element={
              <ScopeRoute require={StaffScope.SCHEDULING_READ}>
                <Appointment />
              </ScopeRoute>
            }
          />
          <Route
            path="/patient-management"
            element={
              <ScopeRoute require={StaffScope.PATIENTS_READ}>
                <PatientManagement />
              </ScopeRoute>
            }
          />
          <Route
            path="/provider-management"
            element={
              <ScopeRoute require={StaffScope.STAFF_READ}>
                <ProviderManagement />
              </ScopeRoute>
            }
          />
          <Route
            path="marketing-management"
            element={
              <ScopeRoute require={StaffScope.MARKETING_READ}>
                <MarketingManagement />
              </ScopeRoute>
            }
          >
            <Route index element={<MarketingOverview />} />
            <Route path="overview" element={<MarketingOverview />} />
            <Route path="campaign" element={<MarketingCampaign />} />
            <Route path="templates" element={<MarketingTemplate />} />
          </Route>
          <Route
            path="/message"
            element={
              <ScopeRoute require={StaffScope.MESSAGING_READ}>
                <Message />
              </ScopeRoute>
            }
          />
          <Route path="/insurance" element={<Insurance />} />
          <Route
            path="/notes"
            element={
              <ScopeRoute require={StaffScope.NOTES_READ}>
                <Notes />
              </ScopeRoute>
            }
          />
          <Route
            path="/files"
            element={
              <ScopeRoute require={StaffScope.FILES_READ}>
                <Files />
              </ScopeRoute>
            }
          />
          {/* <Route path="/tasks" element={<Tasks />} /> */}

          <Route
            path="/image-viewer"
            element={
              <ScopeRoute require={StaffScope.IMAGES_READ}>
                <ImageViewerList />
              </ScopeRoute>
            }
          />
          <Route
            path="/image-viewer/viewer"
            element={
              <ScopeRoute require={StaffScope.IMAGES_READ}>
                <ImageViewer />
              </ScopeRoute>
            }
          />
          <Route
            path="/image-xrayviewer"
            element={
              <ScopeRoute require={StaffScope.IMAGES_READ}>
                <ImageXrayViewer />
              </ScopeRoute>
            }
          />

          <Route
            path="/claims"
            element={
              <ScopeRoute require={StaffScope.CLAIMS_READ}>
                <Claims />
              </ScopeRoute>
            }
          />
          <Route
            path="/charting/*"
            element={
              <ScopeRoute require={StaffScope.CLINICAL_READ}>
                <Charting />
              </ScopeRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ScopeRoute require={StaffScope.BILLING_READ}>
                <Billing />
              </ScopeRoute>
            }
          >
            <Route index element={<Navigate to="ledger" replace />} />
            <Route path="ledger" element={<BillingLedger />} />
            <Route path="invoices" element={<BillingInvoices />} />
            <Route path="payments" element={<BillingPayments />} />
            <Route path="transactions" element={<BillingTransactions />} />
          </Route>
          <Route
            path="/report"
            element={
              <ScopeRoute require={StaffScope.REPORTING_READ}>
                <Reports />
              </ScopeRoute>
            }
          >
            <Route index element={<ReportsHub />} />
            <Route
              path="bundles/:categoryId/:reportSlug/preview"
              element={<ReportPreviewPage />}
            />
            <Route
              path="bundles/:categoryId/:reportSlug"
              element={<ReportsBundleSubmenuDetail />}
            />
            <Route path="bundles/:categoryId" element={<ReportsHub />} />
            <Route path="custom" element={<CustomReportBuilderPage />} />
            <Route path="custom/:id" element={<CustomReportBuilderPage />} />
            <Route path="presets" element={<PresetsAutomationPage />} />
            <Route
              path="scheduled/:id"
              element={<ScheduledReportDetailPage />}
            />
          </Route>
          <Route
            path="/membership/*"
            element={
              <ScopeRoute require={StaffScope.MEMBERSHIP_READ}>
                <Membership />
              </ScopeRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ScopeRoute require={StaffScope.NOTIFICATIONS_READ}>
                <Notifications />
              </ScopeRoute>
            }
          />
          <Route
            path="claims/claims-info"
            element={
              <ScopeRoute require={StaffScope.CLAIMS_READ}>
                <ClaimsInfo />
              </ScopeRoute>
            }
          />
          <Route
            path="/lab"
            element={
              <ScopeRoute require={StaffScope.LAB_READ}>
                <LabPage />
              </ScopeRoute>
            }
          />
          <Route
            path="/form-builder/*"
            element={
              <ScopeRoute require={StaffScope.FORMS_READ}>
                <FormBuilder />
              </ScopeRoute>
            }
          />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route
            path="/settings"
            element={
              <ScopeRoute require={StaffScope.SETTINGS_READ}>
                <Settings />
              </ScopeRoute>
            }
          >
            <Route index element={<></>} />
            <Route
              path="insurance-and-payments/accounting/stripe"
              element={
                <SettingsSubpageGate pageKey="stripe-onboarding">
                  <StripeOnboarding />
                </SettingsSubpageGate>
              }
            />
            {SETTINGS_ROUTE_ENTRIES.map((entry) => {
              const requiredScope = SCOPED_SETTINGS_ITEMS[entry.id]
              const content = <SettingsRouteContent itemId={entry.id} />
              return (
                <Route
                  key={entry.id}
                  path={entry.relativePath}
                  element={
                    requiredScope ? (
                      <ScopeRoute
                        require={requiredScope}
                        redirectTo="/settings"
                      >
                        {content}
                      </ScopeRoute>
                    ) : (
                      content
                    )
                  }
                />
              )
            })}
          </Route>

          <Route path="/ui" element={<Ui />} />
          <Route path="/ui-ds" element={<UiDesignSystem />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/forms/kiosk" element={<KioskForm />} />
        <Route path="/forms/public" element={<PublicFormsViewPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <NoteEditorOverlayHost />
    </>
  )
}

export default AppRoutes
